/* Discovery Credits — small chip that shows in the header when the user
 * has < ∞ credits. Tap → /membership. Live polls the status endpoint at
 * mount and after any /discover response would decrement the number
 * (we do the refresh via a global custom event `wq:credits-changed`).
 *
 * Also owns the "fresh credits landed" celebration toast — when the
 * server flags `credits.refresh_notice`, we fire a Sonner toast and
 * POST /credits/refresh-ack so it never re-fires for the same cycle.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useT } from '../i18n/I18nContext';

export const CREDITS_CHANGED_EVENT = 'wq:credits-changed';

/** Fire from anywhere after a discover attempt so the chip refetches. */
export function announceCreditsChanged() {
  try { window.dispatchEvent(new CustomEvent(CREDITS_CHANGED_EVENT)); } catch { /* ssr */ }
}

export default function CreditsIndicator() {
  const nav = useNavigate();
  const t = useT();
  const [state, setState] = useState(null);
  // Track cycle-starts we've already toasted for so a status refetch
  // during the same session doesn't re-fire the celebration.
  const toastedRef = useRef(new Set());

  const refresh = async () => {
    try {
      const r = await api.get('/credits/status');
      setState(r.data);
      const notice = r.data?.credits?.refresh_notice;
      if (notice?.cycle_start && !toastedRef.current.has(notice.cycle_start)) {
        toastedRef.current.add(notice.cycle_start);
        toast.success(
          t('creditsRefresh.title', '{n} fresh Discovery Credits landed', { n: notice.amount }),
          {
            description: t('creditsRefresh.body', 'Your monthly refresh is in — happy exploring!'),
            duration: 6000,
            icon: '🌿',
          }
        );
        // Fire-and-forget ack so it never re-fires next session.
        api.post('/credits/refresh-ack').catch(() => {});
      }
    } catch { /* silent — endpoint may 401 during transient auth gaps */ }
  };

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener(CREDITS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CREDITS_CHANGED_EVENT, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state) return null;
  if (state.credits?.is_unlimited) return null;

  const total = state.credits?.total_remaining ?? 0;
  const low = total <= 3;
  const zero = total <= 0;

  return (
    <button
      onClick={() => nav('/membership')}
      data-testid="credits-indicator"
      title={`${total} Discovery Credits remaining — tap to view plans`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] border transition ${
        zero ? 'bg-rose-500/15 border-rose-400/40 text-rose-200'
             : low ? 'bg-amber-500/15 border-amber-400/40 text-amber-200'
                   : 'bg-emerald-500/10 border-emerald-400/30 text-emerald-200'
      }`}
    >
      <Sparkles className="w-3 h-3" />
      <span className="font-mono">{total}</span>
      <span className="hidden sm:inline">credit{total === 1 ? '' : 's'}</span>
    </button>
  );
}
